import React, { memo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Award, ImageOff, Clock, ShoppingCart, Check, Download } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { track } from "@amplitude/analytics-browser";
import { cartService } from "../../services/cartService";

// Define the trackEvent function to make it simpler to use
const trackEvent = (
  eventName: string,
  eventProperties?: Record<string, any>
) => {
  console.log(`📊 Tracking event: ${eventName}`, eventProperties);
  try {
    track(eventName, eventProperties);
    return true;
  } catch (error) {
    console.error(`Error tracking event ${eventName}:`, error);
    return false;
  }
};

interface StoryCardProps {
  story: {
    _id: string;
    title: string;
    short_description: string;
    type: string;
    context: string;
    city: string;
    region: string;
    is_sponsored: boolean;
    is_available?: boolean;
    downloads: number;
    sponsor_id?: {
      name: string;
    };
    product_id?: string;
    thumbnail_image?: string;
    key?: string;
  };
  onInfoClick?: () => void;
  onCartUpdate?: () => void; // New callback for cart updates
}

// Add CSS for hover effects - this will be added once during initial mount
const addHoverStyles = () => {
  if (
    typeof document !== "undefined" &&
    !document.getElementById("story-card-hover-styles")
  ) {
    const styleEl = document.createElement("style");
    styleEl.id = "story-card-hover-styles";
    styleEl.textContent = `
      .story-card:hover .action-layer {
        opacity: 1 !important;
        pointer-events: all !important;
      }
      
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }
      
      .cart-notification {
        animation: fadeOut 1s ease-in-out forwards;
        animation-delay: 2s;
      }
    `;
    document.head.appendChild(styleEl);
  }
};

// Only add styles once when component is first loaded
if (typeof document !== "undefined") {
  addHoverStyles();
}

const StoryCard = ({ story, onInfoClick, onCartUpdate }: StoryCardProps) => {
  const { isSignedIn, user } = useUser();

  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [showAddedNotification, setShowAddedNotification] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Derive video path from thumbnail filename (e.g. TheClockmakersDemise.png → TheClockmakersDemise.mp4)
  const getVideoPath = (thumbnailFilename?: string) => {
    if (!thumbnailFilename) return null;
    const cleanFilename = thumbnailFilename.replace(/^\/?(images\/stories\/thumbnails\/)?/, "");
    const baseName = cleanFilename.replace(/\.[^.]+$/, "");
    return `/images/vids/${baseName}.mp4`;
  };

  const videoPath = getVideoPath(story.thumbnail_image);

  const handleMouseEnter = useCallback(() => {
    if (videoRef.current && videoPath) {
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch(() => {
        // Video play failed (no file, autoplay blocked, etc.) — silently ignore
      });
    }
  }, [videoPath]);

  const handleMouseLeave = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsVideoPlaying(false);
    }
  }, []);

  // Determine if the story is available (defaults to true if not specified)
  const isAvailable = story.is_available !== false;

  // Debug function to show the current cart state
  const debugCartState = () => {
    console.log("===== STORYCARD CART DEBUG =====");
    console.log("Story:", story.title, "Product ID:", story.product_id);

    if (
      window.fastspring &&
      window.fastspring.builder &&
      window.fastspring.builder.cart
    ) {
      console.log(
        "FastSpring cart items:",
        window.fastspring.builder.cart.items
      );
      console.log(
        "FastSpring cart count:",
        window.fastspring.builder.cart.items.length
      );
    } else {
      console.log("FastSpring cart not accessible");
    }
    console.log("======================");
  };

  // Use direct cart refresh with better error handling and retry
  const refreshCart = useCallback(() => {
    // Debug the current state
    debugCartState();

    // Try to access FastSpring directly
    if (
      window.fastspring &&
      window.fastspring.builder &&
      window.fastspring.builder.cart
    ) {
      const fsItems = window.fastspring.builder.cart.items || [];
      const fsCount = fsItems.length;

      console.log(`Story card direct FastSpring check: ${fsCount} items`);

      // Update localStorage directly
      const fsProducts = fsItems.map((item) => item.product);
      const newLocalItems = fsProducts.map((productId) => ({
        productId,
        quantity: 1,
      }));

      localStorage.setItem(
        "fastspring_cart_items",
        JSON.stringify(newLocalItems)
      );

      // Call parent update if provided
      if (onCartUpdate) {
        onCartUpdate();
      }

      return fsCount;
    }

    // If FastSpring is not accessible, try to get count from localStorage
    try {
      const storedItems = localStorage.getItem("fastspring_cart_items");
      if (storedItems) {
        const items = JSON.parse(storedItems);
        const count = items.length;
        console.log(`Cart count from localStorage: ${count}`);

        // Call parent update if provided
        if (onCartUpdate) {
          onCartUpdate();
        }

        return count;
      }
    } catch (error) {
      console.error("Error reading cart from localStorage:", error);
    }

    return 0; // Return 0 if no items found
  }, [onCartUpdate]);

  // Track view details click
  const trackViewDetailsClick = () => {
    console.log("📊 Tracking: View Story Details for", story.title);
    trackEvent("View Story Details", {
      story_id: story._id,
      story_title: story.title,
      story_type: story.type,
      story_context: story.context,
      story_location: `${story.city}, ${story.region}`,
      is_sponsored: story.is_sponsored,
      is_available: isAvailable,
      timestamp: new Date().toISOString(),
    });
  };

  // Handle FastSpring add to cart with improved feedback and error handling
  const handleAddToCart = useCallback(() => {
    if (!isAvailable || isAddingToCart) return;

    setIsAddingToCart(true);
    console.log(`Adding to cart: ${story.title} (${story.product_id})`);

    // Track add to cart event FIRST - do this before any async operations
    console.log("📊 Tracking Add To Cart Click for", story.title);
    trackEvent("Add To Cart Click", {
      story_id: story._id,
      story_title: story.title,
      product_id: story.product_id,
      story_type: story.type,
      story_context: story.context,
      price: 19.95, // Add actual price if available
      is_sponsored: story.is_sponsored,
      timestamp: new Date().toISOString(),
    });

    try {
      const imagePath = getImagePath(story.thumbnail_image);

      if (!story.product_id) {
        throw new Error("Missing product ID");
      }

      // Try to add directly to FastSpring
      if (window.fastspring && window.fastspring.builder) {
        window.fastspring.builder.add(story.product_id);
        console.log(`Directly added ${story.product_id} to FastSpring cart`);
      }

      const addedItem = cartService.addToCart(story.product_id, {
        title: story.title,
        imageUrl: imagePath,
      });

      if (addedItem) {
        console.log(`Successfully added ${story.title} to cart`);
        setShowAddedNotification(true);

        // Track successful add to cart
        console.log("📊 Tracking Add To Cart Success for", story.title);
        trackEvent("Add To Cart Success", {
          story_id: story._id,
          product_id: story.product_id,
          story_title: story.title,
          timestamp: new Date().toISOString(),
        });

        // Refresh cart with a delay to allow FastSpring to process
        setTimeout(() => {
          refreshCart();
        }, 300);

        // Hide notification after 3 seconds
        setTimeout(() => {
          setShowAddedNotification(false);
        }, 3000);
      } else {
        console.error(`Failed to add ${story.title} to cart`);
        alert("Failed to add item to cart. Please try again.");

        // Track failure
        console.log("📊 Tracking Add To Cart Failed for", story.title);
        trackEvent("Add To Cart Failed", {
          story_id: story._id,
          product_id: story.product_id,
          story_title: story.title,
          error: "Cart service failure",
          timestamp: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error(`Error adding ${story.title} to cart:`, error);
      alert("There was an error adding the item to your cart.");

      // Track error
      console.log("📊 Tracking Add To Cart Error for", story.title);
      trackEvent("Add To Cart Error", {
        story_id: story._id,
        product_id: story.product_id,
        story_title: story.title,
        error: error instanceof Error ? error.message : "Unknown error",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsAddingToCart(false);
    }
  }, [story, isAvailable, isAddingToCart, refreshCart]);

  // Handle Use Credits click
  const handleUseCredits = () => {
    console.log("📊 Tracking: Use Credits Click for", story.title);
    trackEvent("Use Credits Click", {
      story_id: story._id,
      story_title: story.title,
      credits_used: 10,
      story_type: story.type,
      timestamp: new Date().toISOString(),
    });

    // Your existing credits logic would go here
  };

  // Handle Sponsor Story click
  const handleSponsorClick = () => {
    console.log("📊 Tracking: Sponsor Story Click for", story.title);
    trackEvent("Sponsor Story Click", {
      story_id: story._id,
      story_title: story.title,
      sponsor_price: 19.95,
      story_type: story.type,
      timestamp: new Date().toISOString(),
    });

    // Your existing sponsor logic would go here
  };

  // Construct the full image path
  const getImagePath = (filename?: string) => {
    if (!filename) return "";
    // Remove any leading slashes and the full path if present
    const cleanFilename =
      filename?.replace(/^\/?(images\/stories\/thumbnails\/)?/, "") || "";
    return `/images/stories/thumbnails/${cleanFilename}`;
  };

  return (
    <div
      className="story-card group relative bg-card rounded-sm border border-border/50 border-l-4 border-l-crimson shadow-sm overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-crimson/10"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Story Image + Video */}
      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
        {!imageError ? (
          <img
            src={getImagePath(story.thumbnail_image)}
            alt={story.title}
            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${
              imageLoading ? "opacity-0" : "opacity-100"
            }`}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              console.error("Image load error for:", story.thumbnail_image);
              setImageError(true);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageOff className="h-12 w-12 text-muted-foreground/50" />
          </div>
        )}
        {/* Video overlay — fades in on hover, only if mp4 exists */}
        {videoPath && !videoError && (
          <video
            ref={videoRef}
            src={videoPath}
            muted
            loop
            playsInline
            preload="none"
            onError={() => setVideoError(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              isVideoPlaying ? "opacity-100" : "opacity-0"
            }`}
          />
        )}
        {imageLoading && !imageError && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white font-heading font-semibold truncate">{story.title}</h3>
          <p className="text-white/80 text-sm">
            {story.city}, {story.region}
          </p>
        </div>
      </div>

      {/* Story Info */}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium bg-crimson/10 text-crimson px-2 py-1 rounded-sm">
              {story.type}
            </span>
            <span className="text-xs font-medium bg-gold/10 text-gold px-2 py-1 rounded-sm">
              {story.context}
            </span>
          </div>
          {/* Price - visible on all card sizes, fits with type/context row */}
          {isAvailable && (
            <span className="text-sm font-semibold text-foreground whitespace-nowrap">
              $14.95 USD
            </span>
          )}
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {story.short_description}
        </p>

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            {isAvailable && (
              <>
                <Download className="h-4 w-4" />
                <span>{story.downloads}</span>
              </>
            )}
          </div>

          {!isAvailable ? (
            <div className="flex items-center gap-1 text-yellow-500">
              <Clock className="h-4 w-4" />
              <span>Coming Soon!</span>
            </div>
          ) : (
            <Link
              to={`/stories/${story._id}`}
              className="text-sm font-medium text-crimson hover:text-crimson-dark transition-colors"
              onClick={trackViewDetailsClick}
            >
              Details
            </Link>
          )}
        </div>
      </div>

      {/* Purchase Options */}
      <div className="absolute top-4 right-4 flex flex-col gap-2">
        <button
          className="bg-crimson hover:bg-crimson-dark text-white px-3 py-1 rounded-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
          onClick={handleAddToCart}
          disabled={!isAvailable || isAddingToCart}
          data-testid="add-to-cart-button"
          id="add-to-cart-button"
        >
          <ShoppingCart className="h-3 w-3" />
          {isAvailable
            ? isAddingToCart
              ? "Adding..."
              : "Add to Cart"
            : "Coming Soon!"}
        </button>

        {isSignedIn && (
          <>
            {/* Future feature: credits purchase flow */}
            {/* <button
              disabled={!isSignedIn || !isAvailable}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-3 py-1 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleUseCredits}
            >
              Use 10 Credits
            </button> */}
            {!story.is_sponsored && (
              <button
                className="bg-accent text-accent-foreground hover:bg-accent/90 px-3 py-1 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isAvailable}
                onClick={handleSponsorClick}
              >
                Sponsor $19.95
              </button>
            )}
          </>
        )}
      </div>

      {/* Added to Cart Notification */}
      {showAddedNotification && (
        <div className="cart-notification absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-10 flex items-center gap-2">
          <Check className="h-5 w-5" />
          <span>Added to cart!</span>
        </div>
      )}
    </div>
  );
};

// Use React.memo with proper key comparison
export default memo(StoryCard, (prevProps, nextProps) => {
  // Use story ID for equality check
  return prevProps.story._id === nextProps.story._id;
});
